/**
 * Cart free-gift promo — banner + 3-staps modal.
 *
 * Markup: snippets/cart-free-gift-promo.liquid.
 *
 * De promo rendert op twee plekken: op de cart-pagina (layout `page`) en in de
 * cart drawer (layout `drawer`). Op /cart staan ze allebei tegelijk in de DOM.
 * Dit script bindt daarom *elke* promo apart — elke instantie heeft zijn eigen
 * modal, stap-state en configurator.
 *
 * Voorheen stond dit als inline <script data-section-script> in het snippet, dat
 * bij elke section render opnieuw uitgevoerd werd en het eerste promo-element
 * van de pagina pakte. Met twee instanties werkt dat niet meer; nu wordt het
 * script één keer geladen (layout/theme.liquid) en herbindt het zichzelf op
 * `section:rendered` — de promo verschijnt namelijk pas in de DOM zodra de klant
 * de actiedrempel haalt.
 *
 * Stap 1: kies een product uit de gift-pool.
 * Stap 2: de configurator van dat product wordt uit de PDP gehaald en inline
 *         ingehangen in embed-mode; bij `sh-cfg:added` is het cadeau
 *         toegevoegd en verversen we alleen de cart-secties.
 */
(() => {
  const BOUND = "data-promo-bound";

  // De configurator brengt zijn eigen section-stylesheets mee; die hoeven maar
  // één keer in <head>, ongeacht hoeveel promo-instanties er zijn.
  let stylesInjected = false;

  function injectSectionStyles(doc) {
    if (stylesInjected) return;

    // Inline <style> blocks die de configurator-klasses bevatten
    for (const style of doc.querySelectorAll("style")) {
      const text = style.textContent || "";
      if (
        text.includes("logictrade-configurator") ||
        text.includes("configurator__") ||
        text.includes("viewer__")
      ) {
        const clone = document.createElement("style");
        clone.setAttribute("data-configurator-style", "");
        clone.textContent = text;
        document.head.appendChild(clone);
      }
    }

    // Section-stylesheets die via <link rel="stylesheet"> geladen worden
    const loadedHrefs = new Set(
      [...document.querySelectorAll('link[rel="stylesheet"][href]')].map((l) =>
        l.getAttribute("href"),
      ),
    );
    for (const link of doc.querySelectorAll('link[rel="stylesheet"][href]')) {
      const href = link.getAttribute("href");
      if (!href || loadedHrefs.has(href)) continue;
      const clone = document.createElement("link");
      clone.rel = "stylesheet";
      clone.href = href;
      clone.setAttribute("data-configurator-style", "");
      document.head.appendChild(clone);
    }

    stylesInjected = true;
  }

  function bind(promo) {
    const modal = promo.querySelector("[data-gift-modal]");
    const openBtn = promo.querySelector("[data-open-gift-modal]");
    const closeBtn = promo.querySelector("[data-close-gift-modal]");
    const configContainer = promo.querySelector("[data-gift-config-container]");
    const configLoading = promo.querySelector("[data-gift-config-loading]");
    const stepIndicators = promo.querySelectorAll("[data-step-indicator]");
    const slides = promo.querySelectorAll("[data-gift-modal-slide]");
    if (!modal || !openBtn) return;

    let currentStep = 1;
    let configEl = null;

    const setStep = (step) => {
      currentStep = step;
      slides.forEach((slide) => {
        const s = Number(slide.dataset.giftModalSlide);
        slide.hidden = s !== step;
        if (s === step) slide.setAttribute("aria-current", "step");
        else slide.removeAttribute("aria-current");
      });
      stepIndicators.forEach((el) => {
        el.classList.toggle("is-active", Number(el.dataset.stepIndicator) === step);
      });
    };

    const openModal = () => {
      setStep(1);
      if (typeof modal.showModal === "function") modal.showModal();
      else modal.setAttribute("open", "");
    };

    const clearConfigurator = () => {
      configEl = null;
      if (configContainer) configContainer.innerHTML = "";
    };

    const closeModal = () => {
      modal.close();
      clearConfigurator();
    };

    openBtn.addEventListener("click", openModal);
    closeBtn?.addEventListener("click", closeModal);
    modal.addEventListener("click", (e) => {
      if (e.target === modal) closeModal();
    });
    // Escape sluit de <dialog> zelf; de configurator moet dan ook opgeruimd.
    modal.addEventListener("close", clearConfigurator);

    promo.querySelectorAll("[data-gift-back-to-step]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const target = Number(btn.dataset.giftBackToStep);
        if (!Number.isFinite(target) || target >= currentStep) return;
        clearConfigurator();
        setStep(target);
      });
    });

    const bindConfiguratorEvents = (el) => {
      el.addEventListener("sh-cfg:added", () => {
        closeModal();
        // Het gratis product is via de configurator toegevoegd; alleen de
        // cart-secties opnieuw renderen i.p.v. de pagina herladen. Kwam de
        // promo uit de drawer, dan blijft die gewoon openstaan.
        if (window.HenkCart) {
          window.HenkCart.refresh({ source: "free-gift" }).catch(() =>
            window.location.reload(),
          );
        } else {
          window.location.href = "/cart";
        }
      });
      el.addEventListener("sh-cfg:error", (e) => {
        alert(e.detail?.message || "Er ging iets mis bij het toevoegen.");
      });
    };

    const injectBackButton = (layout) => {
      // Onderaan de optiekolom. Twee generaties configurator, twee klassenamen
      // voor dezelfde kolom; de visual-kolom is de terugval als er geen
      // optiekolom is. Bewust twee losse querySelector-aanroepen: met één
      // komma-selector wint het element dat het eerst in de DOM staat, en dat
      // is juist de visual-kolom.
      const target =
        layout.querySelector(
          ".henk-configurator__options, .configurator__options",
        ) ||
        layout.querySelector(".configurator__visual, .henk-configurator__visual");
      if (!target) return;
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "cart-free-gift-modal__visual-back";
      btn.innerHTML =
        '<svg width="16" height="19" viewBox="0 0 16 19" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.82482 0.353572L0.70717 9.47122L9.82482 18.5889M0.706055 9.46936L15.7061 9.46936" stroke="currentColor" stroke-opacity="0.8"/></svg> Ga terug naar vorige stap';
      btn.addEventListener("click", () => {
        clearConfigurator();
        setStep(1);
      });
      target.appendChild(btn);
    };

    const loadConfigurator = async (url, ltCode, tierCents) => {
      if (!configContainer) return;
      clearConfigurator();
      if (configLoading) configLoading.hidden = false;
      setStep(2);

      try {
        const res = await fetch(url, { credentials: "same-origin" });
        if (!res.ok) throw new Error("Fetch failed: " + res.status);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, "text/html");

        injectSectionStyles(doc);

        // De layout-div hangt op de PDP in een <div class="henk-configurator">,
        // en assets/henk-configurators.css nest álles daaronder
        // (.henk-configurator .henk-configurator__grid { ... }, inclusief de
        // twee-koloms grid vanaf 768px). Kloonden we alleen de layout-div, dan
        // viel die hele nesting weg en kreeg je in de modal één kolom met een
        // uitgerekte viewer. Dus de wrapper meenemen als hij er is.
        const layout =
          doc.querySelector(".henk-configurator") ||
          doc.querySelector(".logictrade-configurator__layout");
        if (!layout) throw new Error("Configurator niet gevonden op product-pagina");

        const layoutClone = document.importNode(layout, true);
        const original = layoutClone.querySelector("logic-configurator");
        if (!original) throw new Error("logic-configurator element ontbreekt");

        // Rename tag to the promo variant so it upgrades against
        // <logic-configurator-promo> instead of the (unloaded) base element.
        const injectedConfig = document.createElement("logic-configurator-promo");
        for (const { name, value } of [...original.attributes]) {
          injectedConfig.setAttribute(name, value);
        }
        while (original.firstChild) injectedConfig.appendChild(original.firstChild);
        original.replaceWith(injectedConfig);

        injectedConfig.dataset.embedMode = "1";
        injectedConfig.dataset.promoGift = "1";
        if (ltCode) injectedConfig.dataset.promoLt = ltCode;
        if (tierCents) injectedConfig.dataset.promoTier = String(tierCents);

        bindConfiguratorEvents(injectedConfig);
        injectBackButton(layoutClone);

        configContainer.appendChild(layoutClone);
        configEl = injectedConfig;

        // Sla de tussenstap (preview/samenvatting) over: eerste klik op
        // add-to-cart in de configurator voert direct de daadwerkelijke add uit.
        configEl._embedConfirmed = true;
      } catch (err) {
        console.error("[gift-modal] loadConfigurator failed:", err);
        if (configContainer) {
          configContainer.innerHTML =
            '<p style="padding:2rem;text-align:center;color:#a04a00">' +
            "Kon de configurator niet laden. Probeer opnieuw of neem contact op." +
            "</p>";
        }
      } finally {
        if (configLoading) configLoading.hidden = true;
      }
    };

    // Stap 1 → 2: click op product laadt direct de configurator
    promo.querySelectorAll("[data-gift-select]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const url = btn.dataset.giftUrl;
        const ltCode = btn.dataset.giftLt || "";
        const tier = btn.dataset.giftTier || "";
        if (!url) return;
        loadConfigurator(url, ltCode, tier);
      });
    });
  }

  /**
   * Bind alles wat nog niet gebonden is. Bij een section render is de promo
   * verse HTML zonder de markering, terwijl de andere instantie (die niet
   * meegeswapt is) hem wél heeft en dus overgeslagen wordt.
   */
  function bindAll() {
    document
      .querySelectorAll(`[data-cart-free-gift-promo]:not([${BOUND}])`)
      .forEach((promo) => {
        promo.setAttribute(BOUND, "");
        bind(promo);
      });
  }

  document.addEventListener("section:rendered", bindAll);
  document.addEventListener("shopify:section:load", bindAll);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bindAll);
  } else {
    bindAll();
  }
})();
